import { reactive } from 'vue'
import type { WorkerProfile } from '../types/models'
import { getCurrentWorker, workerLoginApi, workerLogoutApi } from '../api/workerAuth'

interface WorkerAuthState {
  currentWorker: WorkerProfile | null
  isLoggedIn: boolean
}

export const workerAuthState = reactive<WorkerAuthState>({
  currentWorker: null,
  isLoggedIn: false,
})

const SESSION_KEY = 'worker_session'
const TOKEN_KEY = 'worker_token'

function isActiveWorker(worker: Record<string, any>) {
  const status = worker.status
  return status === 1 || status === '1' || status === 'active' || status === 'enabled' || status == null
}

function normalizeWorker(worker: Record<string, any>): WorkerProfile {
  return {
    ...worker,
    id: String(worker.id || worker.worker_id || ''),
    employee_no: worker.worker_no || worker.workerNo || worker.worker_code || worker.employee_no || String(worker.id || worker.worker_id || ''),
    name: worker.worker_name || worker.name || '未命名工人',
    department_path: worker.department_path || worker.workshop_name || worker.workshopName || '',
  } as WorkerProfile
}

export function isSupervisorWorker(worker: Record<string, any> | null | undefined) {
  if (!worker) return false
  const truthyFlags = [
    worker.is_supervisor,
    worker.isSupervisor,
    worker.supervisor,
    worker.can_report_for_others,
    worker.canReportForOthers,
  ]
  if (truthyFlags.some(flag => flag === true || flag === 1 || flag === '1' || String(flag).toLowerCase() === 'true')) {
    return true
  }

  const roleText = [
    worker.role_code,
    worker.roleCode,
    worker.role,
    worker.position_code,
    worker.positionCode,
    worker.position_name,
    worker.positionName,
    worker.skill_level,
    worker.skillLevel,
    worker.title,
  ].filter(Boolean).join(' ').toLowerCase()

  return [
    'supervisor',
    'workshop_supervisor',
    'line_leader',
    'team_leader',
    'leader',
    'foreman',
    'manager',
    '主管',
    '主任',
    '组长',
    '班长',
  ].some(keyword => roleText.includes(keyword.toLowerCase()))
}

export function isBossMobileWorker(worker: Record<string, any> | null | undefined) {
  if (!worker) return false
  const roleText = [
    worker.role_code,
    worker.roleCode,
    worker.role,
    worker.position_code,
    worker.positionCode,
    worker.position_name,
    worker.positionName,
    worker.skill_level,
    worker.skillLevel,
    worker.title,
    worker.department_path,
    worker.departmentPath,
  ].filter(Boolean).join(' ').toLowerCase()

  return ['boss_admin', 'boss-admin', 'admin', '老板管理员', '系统管理员', '系统管理', '公司移动看板'].some(keyword => roleText.includes(keyword.toLowerCase()))
}

export async function workerLogin(employeeNo: string, password: string): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await workerLoginApi({ employeeNo, password })
    const token = res.data?.token
    const worker = res.data?.worker
    if (!token || !worker || !isActiveWorker(worker)) {
      return { success: false, error: '工号不存在或已离职' }
    }

    localStorage.setItem(TOKEN_KEY, token)

    const currentWorker = normalizeWorker(worker)
    workerAuthState.currentWorker = currentWorker
    workerAuthState.isLoggedIn = true

    localStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
        workerId: currentWorker.id,
        employeeNo: currentWorker.employee_no,
        loginTime: new Date().toISOString(),
      }),
    )

    return { success: true }
  } catch (error: any) {
    localStorage.removeItem(TOKEN_KEY)
    return { success: false, error: error?.message || '网络请求失败' }
  }
}

export function clearWorkerSession() {
  workerAuthState.currentWorker = null
  workerAuthState.isLoggedIn = false
  localStorage.removeItem(SESSION_KEY)
  localStorage.removeItem(TOKEN_KEY)
}

export function workerLogout() {
  workerLogoutApi().catch(() => undefined)
  clearWorkerSession()
}

export async function restoreWorkerSession(): Promise<boolean> {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    const token = localStorage.getItem(TOKEN_KEY)
    if (!raw || !token) return false

    const session = JSON.parse(raw)
    const res = await getCurrentWorker()
    const worker = res.data
    if (!worker || !isActiveWorker(worker) || String(worker.id || worker.worker_id || '') !== String(session.workerId)) {
      workerLogout()
      return false
    }

    workerAuthState.currentWorker = normalizeWorker(worker)
    workerAuthState.isLoggedIn = true
    return true
  } catch {
    workerLogout()
    return false
  }
}
